import { StyleSheet, css } from 'aphrodite';
import { Avatar } from '../../../web/components-v2/Avatar';
import { Button } from '../../../web/components-v2/Button';
import { Pill } from '../../../web/components-v2/Pill';
import { SPACE, THEME } from '../../../web/components-v2/Theme/theme';
import {
	DataGrid,
	DataGridColumn,
	DataGridColumns,
	DataGridRow,
	DataGridRowItem,
	DataGridRows,
} from '../../../web/components/DataGrid';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { accessibilityStyles } from '../../../web/styles/styles';
import { ITimeEntryMissingRep } from '../../queries/TimeEntry';
import { LoadError } from '../../timeTracking/components/LoadError';
import { timeTrackingStyles as tt } from '../../timeTracking/styles';
import { capMissingDays, missingDayChip } from './reviewFilters';

const COLUMN_COUNT = 4;

export function MissingRepsTable({
	reps,
	isLoading,
	isError,
	onRetry,
	nameFor,
	initialsFor,
	onOpenRep,
	isManagerPicked,
}: {
	reps: ITimeEntryMissingRep[];
	isManagerPicked: boolean;
	isLoading: boolean;
	isError: boolean;
	onRetry: () => void;
	nameFor: (userId?: string) => string;
	initialsFor: (userId?: string) => string;
	onOpenRep: (userId: string) => void;
}) {
	return (
		<section className={css(tt.card, tt.tableCard)} aria-label='Reps with missing hours'>
			<DataGrid>
				<DataGridColumns>
					<DataGridColumn sticky>Rep</DataGridColumn>
					<DataGridColumn sticky>Missing</DataGridColumn>
					<DataGridColumn sticky>Days</DataGridColumn>
					<DataGridColumn sticky>
						<span className={css(accessibilityStyles.srOnly)}>Actions</span>
					</DataGridColumn>
				</DataGridColumns>
				<DataGridRows>
					{isLoading ? (
						<DataGridRow>
							<DataGridRowItem colSpan={COLUMN_COUNT}>
								<LoadingSpinner type='large' />
							</DataGridRowItem>
						</DataGridRow>
					) : isError ? (
						<DataGridRow>
							<DataGridRowItem colSpan={COLUMN_COUNT}>
								<LoadError message='We couldn’t load missing hours.' onRetry={onRetry} />
							</DataGridRowItem>
						</DataGridRow>
					) : reps.length ? (
						reps.map(rep => {
							const { shown, hiddenCount } = capMissingDays(rep.days);
							return (
								<DataGridRow key={rep.userId}>
									<DataGridRowItem>
										<span className={css(styles.rep)}>
											<Avatar
												fallbackText={initialsFor(rep.userId)}
												altText={nameFor(rep.userId)}
												smallText
												styles={[styles.avatar]}
											/>
											<span className={css(tt.truncate)}>{nameFor(rep.userId)}</span>
										</span>
									</DataGridRowItem>
									<DataGridRowItem>
										<span className={css(tt.number)}>{rep.days.length}</span>
									</DataGridRowItem>
									<DataGridRowItem>
										<span className={css(styles.days)}>
											{shown.map(day => {
												const { color, label } = missingDayChip(day);
												return <Pill key={day.id} variant='label' color={color} label={label} />;
											})}
											{hiddenCount ? <span className={css(tt.muted, styles.more)}>+{hiddenCount} more</span> : null}
										</span>
									</DataGridRowItem>
									<DataGridRowItem styles={[tt.actionCell]}>
										<Button type='button' variant='outline' size='sm' onClick={() => onOpenRep(rep.userId)}>
											View rep
										</Button>
									</DataGridRowItem>
								</DataGridRow>
							);
						})
					) : (
						<DataGridRow>
							<DataGridRowItem colSpan={COLUMN_COUNT} styles={[tt.emptyCell]}>
								{isManagerPicked ? 'None of this manager’s reps are missing hours.' : 'Everyone’s hours are in.'}
							</DataGridRowItem>
						</DataGridRow>
					)}
				</DataGridRows>
			</DataGrid>
		</section>
	);
}

const styles = StyleSheet.create({
	avatar: {
		flexShrink: 0,
		height: 32,
		width: 32,
	},
	days: {
		display: 'flex',
		flexWrap: 'wrap',
		alignItems: 'center',
		gap: SPACE[4],
	},
	more: {
		fontSize: THEME.typography.fontSizes.xs,
		whiteSpace: 'nowrap',
	},
	rep: {
		alignItems: 'center',
		display: 'flex',
		gap: SPACE[8],
		minWidth: 0,
	},
});
